import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BomV2Meta, BomRow, CreateBomV2Input, ReplaceBomRowsInput } from '@bom/shared';
import { api } from '../lib/apiClient';

/** 列产品内所有扁平 BOM */
export function useBomsV2(tid: string | undefined) {
  return useQuery({
    queryKey: ['boms-v2', tid],
    queryFn: () => api.get<{ boms: BomV2Meta[] }>(`/tenants/${tid}/boms`).then((r) => r.boms),
    enabled: !!tid,
  });
}

export function useBomRows(tid: string | undefined, bid: string | undefined) {
  return useQuery({
    queryKey: ['bom-rows', tid, bid],
    queryFn: () =>
      api.get<{ rows: BomRow[] }>(`/tenants/${tid}/boms/${bid}/rows`).then((r) => r.rows),
    enabled: !!tid && !!bid,
  });
}

/** 新建 BOM（含整表行，Excel 解析后提交） */
export function useCreateBomV2(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBomV2Input) =>
      api.post<{ id: string; row_count: number; notified?: boolean; notify_error?: string }>(
        `/tenants/${tid}/boms`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boms-v2', tid] }),
  });
}

/** 重发 BOM 通知邮件（同步，返回真实结果）；可选自定义主题/正文，留空用默认 */
export function useResendBomNotify(tid: string) {
  return useMutation({
    mutationFn: (vars: { bid: string; subject?: string; body?: string }) =>
      api.post<{ notified: boolean; recipients: number; error?: string }>(
        `/tenants/${tid}/boms/${vars.bid}/notify`,
        {
          ...(vars.subject ? { subject: vars.subject } : {}),
          ...(vars.body ? { body: vars.body } : {}),
        },
      ),
  });
}

/** 整表覆盖某 BOM 的行（旧行存 S3 快照） */
export function useReplaceBomRows(tid: string, bid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReplaceBomRowsInput) =>
      api.post<{
        row_count: number;
        snapshot_key: string | null;
        notified?: boolean;
        notify_error?: string;
      }>(`/tenants/${tid}/boms/${bid}/rows`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bom-rows', tid, bid] });
      qc.invalidateQueries({ queryKey: ['bom-v2', tid, bid] });
      qc.invalidateQueries({ queryKey: ['bom-snapshots', tid, bid] });
    },
  });
}

export function useDeleteBomV2(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bid: string) => api.del<{ deleted: number }>(`/tenants/${tid}/boms/${bid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boms-v2', tid] }),
  });
}
